import React, {Fragment} from 'react';
import styled from 'styled-components';
import NikePOSIcon from '../NikePOSIcon';
import SGBody from 'components/Typography/SGBody';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import Register from 'features/POSRegisters/interfaces/Register';
import DeviceType, {DeviceTypeString} from 'features/POSRegisters/interfaces/DeviceType';
import IconButton from '@material-ui/core/IconButton';
import ConnectedDevices from '../ConnectedDevices';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';

//#region Styled Components
const StyledNodeItem = styled(IconButton)`
  background-color: transparent;
  color: ${props => props.theme.white};
  width: 60px;
  padding: 0;
  border: none;
  cursor: default;

  &.disabled {
    opacity: 1;
  }
`;

const StyledNodeContainer = styled.div<{$area: string; $nodeIndex: number}>`
  background-color: ${props => props.theme.cardBg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  grid-area: ${props => props.$area + '-' + props.$nodeIndex};
  border: none;
  &.disabled {
    opacity: 0.5;
  }
  &.disabled:hover {
    opacity: 0.5;
    cursor: default;
  }
`;

const StyledIconContainer = styled.div`
  position: relative;
  background-color: ${props => props.theme.disabledStateBlue}66;
  min-width: 80px;
  min-height: 80px;
  border-radius: 10px;
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid transparent;
  &.minor {
    border: 2px solid ${props => props.theme.yellow};
  }

  &.major {
    border: 2px solid ${props => props.theme.orange};
  }

  &.critical {
    border: 2px solid ${props => props.theme.red};
  }

  &.scanfailure {
    border: 2px solid ${props => props.theme.informationalBlue};
  }
`;

const StyledLabel = styled(SGBody)`
  font-family: 'Trade Gothic Bold Condensed';
  text-transform: capitalize;
`;

const StyledSeverityBadge = styled(SeverityBadge)`
  position: absolute;
  top: -12px;
  right: -12px;
`;
//#endregion Styled Components

//#region Component
interface NodeItemProps {
  register: Register;
  index: number;
}

export default function NodeItem({register, index}: NodeItemProps) {
  const {ipad, ipadMini, receiptPrinter, barcodeScanner, cashDrawer, paymentTerminal} = register;

  const filteredDevices = [
    ipad,
    ipadMini,
    receiptPrinter,
    barcodeScanner,
    cashDrawer,
    paymentTerminal,
  ].filter(device => device !== undefined);

  const severity = getPriorityAlert(filteredDevices);
  const priority = SeverityPriorityEnum[severity];
  const hasIssues = Number(SeverityPriorityEnum[priority]) > 1;

  return (
    <Fragment key={register.id}>
      <StyledNodeContainer $area={DeviceType.REGISTER} $nodeIndex={index} id={register.id}>
        <StyledIconContainer className={severity.toLowerCase()}>
          {hasIssues && <StyledSeverityBadge severity={severity} size={1} />}
          <StyledNodeItem>
            <NikePOSIcon deviceType={DeviceType.REGISTER} />
          </StyledNodeItem>
          <ConnectedDevices register={register} />
        </StyledIconContainer>
        <StyledLabel variant="b4">
          {DeviceTypeString[DeviceType.REGISTER]}{' '}
          {ipad?.scanResults?.workstationNumber && ipad.scanResults.workstationNumber}
        </StyledLabel>
        {register.id && <SGBody variant="b4">{register.id}</SGBody>}
      </StyledNodeContainer>
    </Fragment>
  );
}
//#endregion Component
